/**
 * VillageChairmanHome — Concern 1 (frontend-foundations)
 * Actor: village_chairman
 * Route: not wired into App.tsx yet — mount at e.g. / (role-specific landing)
 * API: GET /api/v1/customers/dmrv/pending (area-scoped for this role),
 *      GET /api/v1/customers?per_page=25 (tenant-wide list, read-only here)
 *
 * A village chairman's only dMRV responsibility is Step 1 — Community
 * Verification: confirm the customer is a real, known resident of the
 * village before the rest of the pipeline proceeds. This role does not have
 * routing access to full customer detail pages, so the tables below are
 * read-only (no row links).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppBadge, AppButton, AppCard } from "../../../shared/components/ui";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { api } from "../../../shared/api/client";
import { getUser, userDisplayName } from "../../auth/session";

type PendingItem = {
  customer_id: number;
  customer_number: string;
  name: string;
  area_code: string | null;
  dmrv_status: string;
  current_step: number;
  next_pending_step: number | null;
  next_step_label: string | null;
  days_waiting: number;
};

type CustomerRow = {
  id: number;
  first_name: string;
  last_name: string;
  phone: string;
  ward: string | null;
  district: string | null;
};

type SectionState = "loading" | "error" | "success";

export function VillageChairmanHome() {
  const user = getUser();

  const [pending, setPending] = useState<PendingItem[]>([]);
  const [pendingState, setPendingState] = useState<SectionState>("loading");
  const [pendingError, setPendingError] = useState("");

  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [customersState, setCustomersState] = useState<SectionState>("loading");
  const [customersError, setCustomersError] = useState("");

  const loadPending = async () => {
    setPendingState("loading");
    try {
      const d = await api.get<{ pending: PendingItem[]; total: number }>("/customers/dmrv/pending");
      setPending(d.pending ?? []);
      setPendingState("success");
    } catch (ex: unknown) {
      setPendingError(ex instanceof Error ? ex.message : "Failed to load pending verifications");
      setPendingState("error");
    }
  };

  const loadCustomers = async () => {
    setCustomersState("loading");
    try {
      const d = await api.get<{ customers: CustomerRow[]; total: number }>("/customers?per_page=25");
      setCustomers(d.customers ?? []);
      setCustomersState("success");
    } catch (ex: unknown) {
      setCustomersError(ex instanceof Error ? ex.message : "Failed to load customers");
      setCustomersState("error");
    }
  };

  useEffect(() => { loadPending(); loadCustomers(); }, []);

  const pendingListStatus =
    pendingState === "success" && pending.length === 0 ? "empty" : pendingState;
  const customersListStatus =
    customersState === "success" && customers.length === 0 ? "empty" : customersState;

  return (
    <div>
      <PageHeader
        title="Village Chairman"
        subtitle={user ? `Welcome, ${userDisplayName(user)}` : "Community verification and area overview."}
        actions={<Link to="/dmrv"><AppButton type="button">dMRV Dashboard</AppButton></Link>}
      />

      <AppCard title="Step 1 — Community Verification">
        <p style={{ marginTop: 0 }}>
          Confirm each customer is a known resident in your assigned area before the installation pipeline continues.
        </p>
        <ol style={{ margin: 0, paddingLeft: "1.25rem", lineHeight: 1.8 }}>
          <li>Open <Link to="/dmrv">dMRV Dashboard</Link> and find the customer on Step 1.</li>
          <li>Verify identity and residence in your village.</li>
          <li>Enter the customer&apos;s 4-digit PIN and a brief verification note.</li>
          <li>Approve to advance to Step 2, or reject with a reason if you cannot confirm them.</li>
        </ol>
      </AppCard>

      <AppCard title="Pending verification in your area" style={{ marginTop: 16 }}>
        <PageState
          status={pendingListStatus}
          error={pendingError}
          onRetry={loadPending}
          emptyTitle="No pending verifications"
          emptyHint="Customers awaiting Step 1 will appear here."
        >
          <table className="pg-table">
            <thead>
              <tr>
                <th>Customer #</th>
                <th>Name</th>
                <th>Area</th>
                <th>Waiting On</th>
                <th style={{ textAlign: "right" }}>Days Waiting</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((p) => (
                <tr key={p.customer_id}>
                  <td>{p.customer_number}</td>
                  <td>{p.name}</td>
                  <td>{p.area_code ?? "—"}</td>
                  <td>
                    {p.next_step_label
                      ? <AppBadge tone={p.next_pending_step === 1 ? "warning" : "info"}>{p.next_step_label}</AppBadge>
                      : <AppBadge tone="neutral">—</AppBadge>}
                  </td>
                  <td style={{ textAlign: "right" }}>{p.days_waiting}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </PageState>
      </AppCard>

      <AppCard title="Recent Customers" style={{ marginTop: 16 }}>
        <p className="pg-text-muted" style={{ marginTop: 0 }}>
          Read-only summary for your assigned areas.
        </p>
        <PageState
          status={customersListStatus}
          error={customersError}
          onRetry={loadCustomers}
          emptyTitle="No customers"
          emptyHint="Registered customers in your areas will appear here."
        >
          <table className="pg-table">
            <thead><tr><th>Name</th><th>Phone</th><th>Ward</th><th>District</th></tr></thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id}>
                  <td>{c.first_name} {c.last_name}</td>
                  <td>{c.phone}</td>
                  <td>{c.ward ?? "—"}</td>
                  <td>{c.district ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </PageState>
      </AppCard>
    </div>
  );
}
